/**
 * VoiceInput — reusable mic button. Records audio, sends it to
 * /api/voice/transcribe (Smart Whisper: ERP vocabulary + correction pass) and
 * calls `onTranscript(text, meta)` when done. Available to every role.
 *
 * Props:
 *   onTranscript(text, meta) — required
 *   context                  — 'chat' | 'order' | 'reminder' | 'standup' | 'task' (tunes recognition)
 *   size / className / title
 */
import React from 'react';
import { toast } from 'sonner';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import useVoiceRecorder from '../hooks/useVoiceRecorder';

export default function VoiceInput({ onTranscript, context = 'chat', size = 14, className = '', title = 'Tap to speak' }) {
  const { recording, transcribing, toggle } = useVoiceRecorder({
    context,
    onText: (text, meta) => onTranscript(text, meta),
    onError: (msg) => toast.error(msg),
  });

  return (
    <button
      type="button"
      onClick={toggle}
      className={className || `p-1.5 rounded-lg transition-colors ${
        recording ? 'bg-red-500 text-white animate-pulse'
        : transcribing ? 'bg-amber-500 text-black'
        : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
      }`}
      title={recording ? 'Tap to stop' : transcribing ? 'Transcribing…' : title}
      data-testid="voice-input-btn"
      disabled={transcribing}
    >
      {transcribing ? <Loader2 size={size} className="animate-spin" />
        : recording ? <MicOff size={size} />
        : <Mic size={size} />}
    </button>
  );
}
